/**
 * Custom attributes FE/BE alignment utilities.
 *
 * API response shape: custom_attributes[name] = { definition: {...}, value: parsedValue }
 * Request shape (create/update): custom_attributes[name] = rawValue
 *
 * These helpers ensure we only send raw values to the backend and correctly
 * normalize API response for forms and inline updates.
 */

/** API shape: attribute name -> { definition, value } */
export type CustomAttributesApiShape = Record<
    string,
    { definition?: unknown; value?: unknown } | unknown
>;

/**
 * Normalize custom_attributes to request shape: name -> raw value only.
 * Accepts either API response shape ({ definition, value }) or already plain values.
 * Handles legacy array type (returns {}).
 */
export function customAttributesToRequestShape(
    apiShape: CustomAttributesApiShape | Record<string, unknown> | null | undefined | unknown[]
): Record<string, unknown> {
    if (!apiShape || typeof apiShape !== 'object' || Array.isArray(apiShape)) return {};

    return Object.fromEntries(
        Object.entries(apiShape).map(([name, entry]) => {
            const rawValue =
                entry !== null &&
                typeof entry === 'object' &&
                'value' in entry &&
                (entry as { value?: unknown }).value !== undefined
                    ? (entry as { value: unknown }).value
                    : entry;
            return [name, rawValue];
        })
    );
}

/**
 * Normalize custom_attributes for form defaultValues: name -> value.
 * Same as request shape; use so form fields receive raw values, not { definition, value }.
 */
export function normalizeCustomAttributesForForm(
    apiShape: CustomAttributesApiShape | Record<string, unknown> | null | undefined | unknown[]
): Record<string, unknown> {
    return customAttributesToRequestShape(apiShape);
}
